/**
 * Promos Listing Page
 * 
 * Main page for managing promo codes with:
 * - Table listing all promos
 * - Filters by status, domain, transaction type, campaign
 * - Search functionality
 * - Pagination
 * - Actions (create, edit, archive, activate/deactivate)
 */

import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { Container, Card, Row, Col, Alert } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';

// Common components
import Breadcrumbs from '../../../Common/Breadcrumbs';
import PageHeader from '../../../Common/PageHeader';
import { SuccessModal } from '../../../Common/SuccessModal';
import { DeleteModal } from '../../../Common/DeleteModal';
import ShimmerLoader from '../../../Common/ShimmerLoader';
import FilterSidebar, { FilterValues, FilterConfig } from '../../../Common/FilterSidebar';

// Hooks
import { useDebounce } from '../../../hooks/useDebounce';

// Promo specific components
import PromoTable from './components/PromoTable';
import Pagination from '../../../pages/ShopServices/Orders/components/Pagination';
import './Promos.css';

// API and types
import { getPromos, deletePromo, updatePromoStatus } from '../../../api/promos';
import { Promo, PromoListParams, PromoStatus, Domain, TransactionType } from '../../../api/promos/types';
import { useSelector } from 'react-redux';
import { hasAnyPermission, type User } from '../../../utils/roleBasedAccess';

const PromosPage: React.FC = () => {
  const navigate = useNavigate();
  const { user, role, permissions } = useSelector((state: any) => state.Login);
  const userData: User = useMemo(
    () => ({
      ...(user || {}),
      subRole: (user && user.subRole) || role,
      permissions: permissions,
    }),
    [user, role, permissions]
  );
  const hasManagePermissions = hasAnyPermission(userData, ['promo.admin']);
  
  // State management
  const [promos, setPromos] = useState<Promo[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [totalCount, setTotalCount] = useState(0);
  // Removed duplicate currentPage state - using filters.pageNum as single source of truth
  
  // Filters state
  const [filters, setFilters] = useState<PromoListParams>({
    status: undefined,
    domain: undefined,
    transactionType: undefined,
    campaignId: undefined,
    search: '',
    pageNum: 1,
    pageSize: 10,
    sortBy: 'createdAt',
    sortOrder: 'desc',
    startAt: undefined,
    endAt: undefined,
  });
  
  // Derived value for currentPage (using filters.pageNum as source of truth)
  const currentPage = filters.pageNum || 1;
  
  // Modal states
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successTitle, setSuccessTitle] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedPromo, setSelectedPromo] = useState<Promo | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  
  // Filter sidebar state
  const [showFilterSidebar, setShowFilterSidebar] = useState(false);
  const [filterValues, setFilterValues] = useState<FilterValues>({});
  
  // Search input state (for immediate UI updates)
  const [searchInputValue, setSearchInputValue] = useState<string>(filters.search || '');
  
  // Debounce search input to prevent excessive API calls (300ms delay)
  const debouncedSearchTerm = useDebounce(searchInputValue, 300);
  
  // AbortController ref for request cancellation
  const abortControllerRef = useRef<AbortController | null>(null);
  
  // Sync filterValues with filters when filters change (but only if sidebar is closed to avoid conflicts)
  useEffect(() => {
    if (!showFilterSidebar) {
      setFilterValues({
        status: filters.status || undefined,
        domain: filters.domain || undefined,
        transactionType: filters.transactionType || undefined,
        campaignId: filters.campaignId || undefined,
        startDate: filters.startAt || undefined,
        endDate: filters.endAt || undefined,
      });
    }
  }, [filters, showFilterSidebar]);
  
  // Sync searchInputValue with filters.search when filters change externally (e.g., from filter sidebar)
  useEffect(() => {
    if (filters.search !== searchInputValue && filters.search !== debouncedSearchTerm) {
      setSearchInputValue(filters.search || '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.search]);
  
  // Trigger search when debounced value changes
  useEffect(() => {
    if (debouncedSearchTerm !== filters.search) {
      handleFilterChange({ search: debouncedSearchTerm });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearchTerm]);
  
  // Fetch promos data
  const fetchPromos = useCallback(async () => {
    // Cancel any ongoing request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    
    // Create new AbortController for this request
    const abortController = new AbortController();
    abortControllerRef.current = abortController;
    
    try {
      setLoading(true);
      setError(null);
      
      const response = await getPromos(filters);
      
      // Check if request was aborted
      if (abortController.signal.aborted) {
        return;
      }
      
      setPromos(response.promos || []);
      setTotalCount(response.totalCount || 0);
      // Removed setCurrentPage - currentPage is now derived from filters.pageNum
    } catch (err: any) {
      // Don't set error if request was aborted (user changed filters or component unmounted)
      if (abortController.signal.aborted || err.name === 'AbortError' || err.code === 'ERR_CANCELED') {
        return;
      }
      setError(err.message || 'Failed to load promos. Please try again.');
    } finally {
      // Only update loading state if request wasn't aborted
      if (!abortController.signal.aborted) {
        setLoading(false);
      }
    }
  }, [filters]);
  
  // Load promos on mount and filter changes
  useEffect(() => {
    fetchPromos();
    
    // Cleanup: cancel request if component unmounts or filters change
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [fetchPromos]);
  
  // Handle filter changes
  const handleFilterChange = useCallback((newFilters: Partial<PromoListParams>) => {
    setFilters(prev => ({
      ...prev,
      ...newFilters,
      pageNum: 1, // Reset to first page when filters change
    }));
  }, []);
  
  // Handle search input change (immediate UI update, debounced API call)
  const handleSearchInputChange = useCallback((searchTerm: string) => {
    setSearchInputValue(searchTerm);
  }, []);
  
  // Handle search clear
  const handleSearchClear = useCallback(() => {
    setSearchInputValue('');
    handleFilterChange({ search: '' });
  }, [handleFilterChange]);
  
  // Handle pagination
  const handlePageChange = useCallback((page: number) => {
    setFilters(prev => ({
      ...prev,
      pageNum: page,
    }));
  }, []);
  
  // Handle page size change
  const handlePageSizeChange = useCallback((size: number) => {
    setFilters(prev => ({
      ...prev,
      pageSize: size,
      pageNum: 1, // Reset to first page
    }));
  }, []);
  
  // Handle create new promo
  const handleCreatePromo = () => {
    if (!hasManagePermissions) return;
    navigate('/promos/promos/create');
  };
  
  // Handle edit promo
  const handleEditPromo = useCallback((promo: Promo) => {
    navigate(`/promos/promos/${promo.id}/edit`);
  }, [navigate]);
  
  // Handle view promo
  const handleViewPromo = useCallback((promo: Promo) => {
    navigate(`/promos/promos/${promo.id}/detail`, { state: { activeTab: 'overview' } });
  }, [navigate]);

  // Handle manage assignments navigation
  const handleManageAssignments = useCallback((promo: Promo) => {
    navigate(`/promos/promos/${promo.id}/detail`, { state: { activeTab: 'assignments' } });
  }, [navigate]);
  
  // Handle delete promo
  const handleDeletePromo = useCallback((promo: Promo) => {
    setSelectedPromo(promo);
    setShowDeleteModal(true);
  }, []);
  
  // Confirm delete promo
  const confirmDeletePromo = async () => {
    if (!selectedPromo) return;
    
    try {
      setIsDeleting(true);
      await deletePromo(selectedPromo.id);
      
      setShowDeleteModal(false);
      setSelectedPromo(null);
      
      // Show success message
      setSuccessTitle('Promo Deleted');
      setSuccessMessage(`Promo "${selectedPromo.name}" has been successfully deleted.`);
      setShowSuccessModal(true);
      
      // Refresh promos list
      fetchPromos();
    } catch (err: any) {
      setError(err.message || 'Failed to delete promo. Please try again.');
    } finally {
      setIsDeleting(false);
    }
  };
  
  // Handle archive promo (using update-status endpoint)
  const handleArchivePromo = useCallback(async (promo: Promo) => {
    try {
      await updatePromoStatus(promo.id, PromoStatus.ARCHIVED);
      
      setSuccessTitle('Promo Archived');
      setSuccessMessage(`Promo "${promo.name}" has been archived successfully.`);
      setShowSuccessModal(true);
      
      // Refresh promos list
      fetchPromos();
    } catch (err: any) {
      setError(err.message || 'Failed to archive promo. Please try again.');
    }
  }, [fetchPromos]);
  
  // Handle activate/deactivate promo (Activate/Deactivate)
  const handleToggleStatus = useCallback(async (promo: Promo) => {
    try {
      // Determine new status
      const newStatus = promo.status === PromoStatus.ACTIVE 
        ? PromoStatus.INACTIVE 
        : PromoStatus.ACTIVE;
      
      // Use update-status endpoint for status changes
      await updatePromoStatus(promo.id, newStatus);
      
      const action = newStatus === PromoStatus.ACTIVE ? 'Activated' : 'Deactivated';
      setSuccessTitle(`Promo ${action}`);
      setSuccessMessage(`Promo "${promo.name}" has been ${action.toLowerCase()}.`);
      
      setShowSuccessModal(true);
      
      // Refresh promos list
      fetchPromos();
    } catch (err: any) {
      setError(err.message || 'Failed to update promo status. Please try again.');
    }
  }, [fetchPromos]);
  
  const handleDuplicatePromo = useCallback((promo: Promo) => {
    navigate('/promos/promos/create', {
      state: { duplicateFrom: promo }
    });
  }, [navigate]);
  
  // Handle filter sidebar
  const handleOpenFilterSidebar = () => {
    setShowFilterSidebar(true);
  };
  
  const handleCloseFilterSidebar = () => {
    setShowFilterSidebar(false);
  };
  
  const handleFilterChangeLocal = (key: string, value: string) => {
    setFilterValues(prev => ({
      ...prev,
      [key]: value || undefined,
    }));
  };
  
  const handleApplyFilters = (filters: FilterValues) => {
    // Validate date range before applying
    if (filters.startDate && filters.endDate && filters.endDate < filters.startDate) {
      // Don't apply filters if date range is invalid
      // The FilterSidebar component will show validation error
      return;
    }
    
    setFilterValues(filters);
    const newFilters: Partial<PromoListParams> = {};
    
    if (filters.status && filters.status !== 'all') {
      newFilters.status = filters.status as PromoStatus;
    }
    if (filters.domain && filters.domain !== 'all') {
      newFilters.domain = filters.domain as Domain;
    }
    if (filters.transactionType && filters.transactionType !== 'all') {
      newFilters.transactionType = filters.transactionType as TransactionType;
    }
    if (filters.campaignId && filters.campaignId !== 'all') {
      newFilters.campaignId = filters.campaignId;
    }
    if (filters.startDate) {
      newFilters.startAt = filters.startDate;
    }
    if (filters.endDate) {
      newFilters.endAt = filters.endDate;
    }
    
    handleFilterChange(newFilters);
    handleCloseFilterSidebar();
  };
  
  const handleResetFilters = () => {
    setFilterValues({});
    handleFilterChange({
      status: undefined,
      domain: undefined,
      transactionType: undefined,
      campaignId: undefined,
      startAt: undefined,
      endAt: undefined,
    });
    handleCloseFilterSidebar();
  };
  
  // Filter configuration
  const filterConfig: FilterConfig = {
    status: {
      label: 'Status',
      options: [
        { value: 'all', label: 'All' },
        { value: PromoStatus.ACTIVE, label: 'Active' },
        { value: PromoStatus.PAUSED, label: 'Paused' },
        { value: PromoStatus.DRAFT, label: 'Draft' },
        { value: PromoStatus.ARCHIVED, label: 'Archived' },
        { value: PromoStatus.EXPIRED, label: 'Expired' },
      ],
    },
    domain: {
      label: 'Domain',
      options: [
        { value: 'all', label: 'All' },
        { value: Domain.SHOP, label: 'Shop' },
        { value: Domain.ACADEMY, label: 'Academy' },
        { value: Domain.VENUE, label: 'Venue' },
      ],
    },
    transactionType: {
      label: 'Transaction Type',
      options: [
        { value: 'all', label: 'All' },
        { value: TransactionType.SHOP_PURCHASE, label: 'Shop Purchase' },
        { value: TransactionType.ACADEMY_ENROLLMENT, label: 'Academy Enrollment' },
        { value: TransactionType.VENUE_BOOKING, label: 'Venue Booking' },
      ],
    },
    dateRange: {
      label: 'Date Range',
      startDateLabel: 'Start Date',
      endDateLabel: 'End Date',
    },
  };
  
  return (
    <div className="page-content">
      <Container fluid>
        {/* Breadcrumbs */}
        <div className="mb-3" style={{ padding: '0 15px' }}>
          <Breadcrumbs 
            items={[
              { label: 'Promos', onClick: () => navigate('/promos') },
              { label: 'All Promos', active: true }
            ]} 
          />
        </div>
        
        {/* Page Header */}
        <div className="mb-4" style={{ padding: '0 15px' }}>
          <PageHeader
            breadcrumb={null}
            title="All Promos"
            addNewLabel={hasManagePermissions ? "+ Create Promo" : undefined}
            onAddNew={hasManagePermissions ? handleCreatePromo : undefined}
          />
        </div>
        
        {/* Error Alert */}
        {error && (
          <div className="mb-3" style={{ padding: '0 15px' }}>
            <Alert variant="danger" onClose={() => setError(null)} dismissible>
              {error}
            </Alert>
          </div>
        )}
        
        {/* Main Content */}
        <Row>
          <Col>
            <Card>
              <Card.Body style={{ padding: '24px' }}>
                {/* Search and Filter Bar */}
                <div className="d-flex justify-content-between align-items-center mb-3" style={{ gap: '16px' }}>
                  {/* Search Bar */}
                  <div className="flex-grow-1" style={{ maxWidth: '400px' }}>
                    <div className="position-relative">
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Search promos..."
                        value={searchInputValue}
                        onChange={(e) => handleSearchInputChange(e.target.value)}
                        onFocus={(e) => {
                          e.currentTarget.style.borderColor = '#E5E7EB';
                          e.currentTarget.style.boxShadow = 'none';
                          e.currentTarget.style.outline = 'none';
                        }}
                        style={{
                          paddingLeft: '40px',
                          height: '40px',
                          border: '1px solid #E5E7EB',
                          borderRadius: '8px',
                          fontFamily: 'Helvetica Neue, Arial, sans-serif',
                          fontSize: '14px',
                        }}
                      />
                      <i 
                        className="ph-magnifying-glass" 
                        style={{
                          position: 'absolute',
                          left: '12px',
                          top: '50%',
                          transform: 'translateY(-50%)',
                          color: '#6B7280',
                          fontSize: '18px',
                        }}
                      />
                      {searchInputValue && (
                        <button
                          type="button"
                          className="btn btn-link p-0"
                          onClick={handleSearchClear}
                          style={{
                            position: 'absolute',
                            right: '8px',
                            top: '50%',
                            transform: 'translateY(-50%)',
                            color: '#6B7280',
                            padding: '4px',
                            border: 'none',
                            background: 'none',
                            cursor: 'pointer',
                          }}
                        >
                          <i className="ph-x" style={{ fontSize: '16px' }}></i>
                        </button>
                      )}
                    </div>
                  </div>
                  
                  {/* Filter Button */}
                  <button
                    type="button"
                    onClick={handleOpenFilterSidebar}
                    className="btn btn-outline-secondary d-flex align-items-center gap-2"
                    style={{
                      height: '40px',
                      padding: '8px 16px',
                      border: '1px solid #E5E7EB',
                      borderRadius: '8px',
                      backgroundColor: '#FFFFFF',
                      color: '#374151',
                      fontFamily: 'Helvetica Neue, Arial, sans-serif',
                      fontSize: '14px',
                      fontWeight: 500,
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#F9FAFB'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#FFFFFF'; }}
                    title="Filter"
                    aria-label="Filter"
                  >
                    <img
                      src="https://cdn-dev.sportsforlife.in/admin/static-assets/icons/general/filter-Icon.svg"
                      alt="Filter"
                      style={{ width: '20px', height: '20px' }}
                      onError={(e) => {
                        const target = e.target as HTMLImageElement;
                        target.style.display = 'none';
                        const icon = document.createElement('i');
                        icon.className = 'ph-funnel';
                        icon.style.fontSize = '18px';
                        icon.style.color = '#374151';
                        target.parentElement?.appendChild(icon);
                      }}
                    />
                    Filter
                  </button>
                </div>
                
                {/* Table */}
                {loading ? (
                  <ShimmerLoader type="table" rows={5} />
                ) : promos.length > 0 ? (
                <PromoTable
                  promos={promos}
                  totalCount={totalCount}
                  currentPage={currentPage}
                  pageSize={filters.pageSize || 10}
                  onPageChange={handlePageChange}
                  onPageSizeChange={handlePageSizeChange}
                  onView={handleViewPromo}
                  onEdit={hasManagePermissions ? handleEditPromo : undefined}
                  onDelete={hasManagePermissions ? handleDeletePromo : undefined}
                  onArchive={hasManagePermissions ? handleArchivePromo : undefined}
                  onToggleStatus={hasManagePermissions ? handleToggleStatus : undefined}
                  onDuplicate={hasManagePermissions ? handleDuplicatePromo : undefined}
                  onManageAssignments={hasManagePermissions ? handleManageAssignments : undefined}
                />
                ) : (
                  <div className="text-center py-5">
                    <p className="text-muted mb-0">No promos found. Create your first promo code!</p>
                  </div>
                )}
              </Card.Body>
            </Card>
            
            {/* Pagination - Outside Card to match Products page structure */}
            {!loading && Math.ceil(totalCount / (filters.pageSize || 10)) > 1 && (
              <Pagination
                currentPage={currentPage}
                totalPages={Math.ceil(totalCount / (filters.pageSize || 10))}
                totalCount={totalCount}
                pageSize={filters.pageSize || 10}
                onPageChange={handlePageChange}
                onPageSizeChange={handlePageSizeChange}
              />
            )}
          </Col>
        </Row>
        
        {/* Filter Sidebar */}
        <FilterSidebar
          show={showFilterSidebar}
          onClose={handleCloseFilterSidebar}
          filterConfig={filterConfig}
          filters={filterValues}
          onFilterChange={handleFilterChangeLocal}
          onApply={handleApplyFilters}
          onReset={handleResetFilters}
        />
        
        {/* Success Modal */}
        <SuccessModal
          show={showSuccessModal}
          handleClose={() => setShowSuccessModal(false)}
          title={successTitle}
          message={successMessage}
        />
        
        {/* Delete Confirmation Modal */}
        <DeleteModal
          show={showDeleteModal}
          handleClose={() => {
            setShowDeleteModal(false);
            setSelectedPromo(null);
          }}
          deleteModalFunction={confirmDeletePromo}
          isLoading={isDeleting}
          title="Delete Promo"
          message={`Are you sure you want to delete "${selectedPromo?.name}"? This action cannot be undone.`}
        />
      </Container>
    </div>
  );
};

export default PromosPage;
